import { useState, useMemo } from "react";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    ReferenceLine,
} from "recharts";
import type { LifetimeProjection } from "../domain/types";

type Metric = "generation" | "co2e" | "economic" | "health" | "ecosystem";
type ViewMode = "annual" | "cumulative";

const METRIC_CONFIG: Record<
    Metric,
    { label: string; color: string; unit: string; valueKey: string; totalKey: string; format: (v: number) => string }
> = {
    generation: {
        label: "Generation",
        color: "#00c77b",
        unit: "MWh",
        valueKey: "projectedGenerationMwh",
        totalKey: "lifetimeGenerationGwh",
        format: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)} GWh` : `${v.toFixed(0)} MWh`,
    },
    economic: {
        label: "Economic Value",
        color: "#22C55E",
        unit: "USD",
        valueKey: "economicValueNpvUsd",
        totalKey: "lifetimeEconomicNpvUsd",
        format: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
            if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
            return `$${v.toFixed(0)}`;
        },
    },
    co2e: {
        label: "Avoided CO\u2082e",
        color: "#00b4eb",
        unit: "tonnes",
        valueKey: "avoidedCo2eKg",
        totalKey: "lifetimeAvoidedCo2eTonnes",
        format: (v) => {
            const tonnes = Math.abs(v) / 1000;
            if (tonnes >= 1000) return `${(tonnes / 1000).toFixed(1)}k t`;
            return `${tonnes.toFixed(1)} t`;
        },
    },
    health: {
        label: "Health (DALYs)",
        color: "#FBBF24",
        unit: "DALYs",
        valueKey: "avoidedDalys",
        totalKey: "lifetimeAvoidedDalys",
        format: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1) return `${v.toFixed(2)}`;
            return `${(v * 1000).toFixed(1)} mDALY`;
        },
    },
    ecosystem: {
        label: "Ecosystem (PDFs)",
        color: "#3B82F6",
        unit: "PDFs",
        valueKey: "avoidedPdfs",
        totalKey: "lifetimeAvoidedPdfs",
        format: (v) => {
            const abs = Math.abs(v);
            if (abs >= 1) return `${v.toFixed(4)}`;
            return v.toExponential(2);
        },
    },
};

const METRICS: Metric[] = ["generation", "economic", "co2e", "health", "ecosystem"];

interface LifetimeProjectionChartProps {
    projection: LifetimeProjection;
    metric?: Metric;
    defaultMode?: "nominal" | "npv";
}

export function LifetimeProjectionChart({
    projection,
    metric: initialMetric = "economic",
    defaultMode = "npv",
}: LifetimeProjectionChartProps) {
    const [activeMetric, setActiveMetric] = useState<Metric>(initialMetric);
    const [mode, setMode] = useState<"nominal" | "npv">(defaultMode);
    const [viewMode, setViewMode] = useState<ViewMode>("cumulative");

    const config = METRIC_CONFIG[activeMetric];

    const chartData = useMemo(() => {
        let cumulative = 0;
        return projection.yearlyProjections.map((yr) => {
            let value: number;
            if (activeMetric === "economic") {
                value = mode === "npv" ? yr.economicValueNpvUsd : yr.economicValueNominalUsd;
            } else {
                value = yr[config.valueKey as keyof typeof yr] as number;
            }
            const absValue = Math.abs(value);
            cumulative += absValue;

            return {
                year: yr.year,
                value: absValue,
                cumulative,
                label: config.format(value),
            };
        });
    }, [projection.yearlyProjections, activeMetric, mode, config]);

    // For cumulative view: compute what a linear accumulation would look like
    // (constant annual value = Year 1 value every year)
    const linearReference = useMemo(() => {
        if (viewMode !== "cumulative" || chartData.length === 0) return undefined;
        const year1Value = chartData[0].value;
        const lastYear = chartData.length;
        return year1Value * lastYear; // what cumulative total would be without decay
    }, [viewMode, chartData]);

    const lifetimeTotal = useMemo(() => {
        if (activeMetric === "economic") {
            return mode === "npv"
                ? projection.lifetimeEconomicNpvUsd
                : projection.lifetimeEconomicNominalUsd;
        }
        return projection[config.totalKey as keyof LifetimeProjection] as number;
    }, [projection, activeMetric, mode, config]);

    const formatLifetimeTotal = (v: number): string => {
        if (activeMetric === "generation") {
            return `${Math.abs(v).toFixed(1)} GWh`;
        }
        if (activeMetric === "economic") {
            const abs = Math.abs(v);
            if (abs >= 1e6) return `$${(Math.abs(v) / 1e6).toFixed(1)}M`;
            return `$${(Math.abs(v) / 1e3).toFixed(0)}k`;
        }
        if (activeMetric === "co2e") {
            return `${Math.abs(v).toFixed(0)} t CO\u2082e`;
        }
        return config.format(v);
    };

    const dataKey = viewMode === "cumulative" ? "cumulative" : "value";

    return (
        <div className="mt-6 rounded-xl bg-surface-700 border border-border p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">
                    Lifetime Projection
                </h3>
                <div className="flex gap-2">
                    {/* Annual / Cumulative toggle */}
                    <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                        <button
                            onClick={() => setViewMode("annual")}
                            className={`px-3 py-1.5 transition-colors ${
                                viewMode === "annual"
                                    ? "bg-surface-500 text-text-primary"
                                    : "bg-surface-700 text-text-secondary hover:text-text-primary"
                            }`}
                        >
                            Annual
                        </button>
                        <button
                            onClick={() => setViewMode("cumulative")}
                            className={`px-3 py-1.5 transition-colors ${
                                viewMode === "cumulative"
                                    ? "bg-surface-500 text-text-primary"
                                    : "bg-surface-700 text-text-secondary hover:text-text-primary"
                            }`}
                        >
                            Cumulative
                        </button>
                    </div>
                    {/* Nominal / NPV toggle (economic only) */}
                    {activeMetric === "economic" && (
                        <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                            <button
                                onClick={() => setMode("nominal")}
                                className={`px-3 py-1.5 transition-colors ${
                                    mode === "nominal"
                                        ? "bg-surface-500 text-text-primary"
                                        : "bg-surface-700 text-text-secondary hover:text-text-primary"
                                }`}
                            >
                                Nominal
                            </button>
                            <button
                                onClick={() => setMode("npv")}
                                className={`px-3 py-1.5 transition-colors ${
                                    mode === "npv"
                                        ? "bg-surface-500 text-text-primary"
                                        : "bg-surface-700 text-text-secondary hover:text-text-primary"
                                }`}
                            >
                                NPV (3%)
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Metric tabs */}
            <div className="flex gap-1 mb-4 flex-wrap">
                {METRICS.map((m) => (
                    <button
                        key={m}
                        onClick={() => setActiveMetric(m)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            activeMetric === m
                                ? "text-white"
                                : "bg-surface-600 text-text-secondary hover:text-text-primary"
                        }`}
                        style={activeMetric === m ? { backgroundColor: METRIC_CONFIG[m].color + "33", color: METRIC_CONFIG[m].color } : undefined}
                    >
                        {METRIC_CONFIG[m].label}
                    </button>
                ))}
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <defs>
                        <linearGradient id={`grad-${activeMetric}-${viewMode}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={config.color} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={config.color} stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis
                        dataKey="year"
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false}
                        axisLine={{ stroke: "#334155" }}
                    />
                    <YAxis
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => config.format(v)}
                        width={70}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #334155",
                            borderRadius: "8px",
                            fontSize: "12px",
                            color: "#e2e8f0",
                        }}
                        formatter={(value: number) => [
                            config.format(value),
                            viewMode === "cumulative" ? `${config.label} (cumulative)` : config.label,
                        ]}
                        labelFormatter={(label) => `Year ${label}`}
                    />
                    {/* In cumulative mode: dashed line showing linear (no-decay) reference */}
                    {viewMode === "cumulative" && linearReference != null && chartData.length > 0 && (
                        <ReferenceLine
                            segment={[
                                { x: chartData[0].year, y: chartData[0].value },
                                { x: chartData[chartData.length - 1].year, y: linearReference },
                            ]}
                            stroke="#64748b"
                            strokeDasharray="6 4"
                            strokeWidth={1}
                            label={{
                                value: "No decay",
                                position: "insideTopRight",
                                fill: "#64748b",
                                fontSize: 10,
                            }}
                        />
                    )}
                    <Area
                        type="monotone"
                        dataKey={dataKey}
                        stroke={config.color}
                        fill={`url(#grad-${activeMetric}-${viewMode})`}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, fill: config.color }}
                    />
                </AreaChart>
            </ResponsiveContainer>

            {/* Summary row */}
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                <div>
                    <p className="text-xs text-text-secondary">Project Lifetime</p>
                    <p className="text-sm font-semibold text-text-primary">
                        {projection.config.projectLifetimeYears} years
                    </p>
                </div>
                <div>
                    <p className="text-xs text-text-secondary">
                        Lifetime {config.label}
                    </p>
                    <p className="text-sm font-semibold" style={{ color: config.color }}>
                        {formatLifetimeTotal(lifetimeTotal)}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-text-secondary">Annual Degradation</p>
                    <p className="text-sm font-semibold text-text-primary">
                        {(projection.config.annualDegradationPct * 100).toFixed(1)}%
                    </p>
                </div>
            </div>
        </div>
    );
}
